export interface ApiErrorBody {
  code?: string
  message?: string
  details?: unknown
  requestId?: string
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function cookie(name: string): string {
  const prefix = `${name}=`
  const value = document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(prefix))
  return value ? decodeURIComponent(value.slice(prefix.length)) : ''
}

export async function ensureCsrf(): Promise<void> {
  await api<void>('/api/v1/auth/csrf')
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  const requestId = headers.get('X-Request-ID') || crypto.randomUUID()
  headers.set('X-Request-ID', requestId)
  // The browser supplies the multipart boundary; do not label uploads as JSON.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('X-CSRFToken', cookie('coldwise_csrf'))

  let response: Response
  try {
    response = await fetch(path, { ...init, headers, credentials: 'same-origin' })
  } catch (cause) {
    if (cause instanceof Error && cause.name === 'AbortError') throw cause
    throw new ApiError('Network request failed', 0, {
      code: 'NETWORK_ERROR', message: 'Network request failed', requestId,
    })
  }
  if (response.status === 204) return undefined as T
  const body = (await response.json().catch(() => ({}))) as T & ApiErrorBody
  if (!response.ok) {
    body.requestId ||= response.headers.get('X-Request-ID') || requestId
    throw new ApiError(body.message || `HTTP ${response.status}`, response.status, body)
  }
  return body
}
